// src/pages/Trainer/CourseManagement/Components/CreateCourse/SectionsLessonsTab.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, ChevronDown, ChevronRight } from 'lucide-react';
import {
  addSection,
  getSections,
  addLesson,
  updateLesson,
  deleteLesson,
  type Section,
  type Lesson,
} from '@/services/AdminService/AdminCourseManagementService';
import {
  getAssessments,
  type Assessment,
} from '@/services/PagesServices/AssessmentManagementService';
import './CourseCreationTabs.css';

interface SectionsLessonsTabProps {
  courseId?: number;
  onUpdate?: (sections: SectionWithLessons[]) => void;
}

interface SectionWithLessons extends Section {
  lessons: Lesson[];
  isExpanded?: boolean;
}

interface LessonFormData extends Lesson {
  isEditing?: boolean;
}

const SectionsLessonsTab: React.FC<SectionsLessonsTabProps> = ({ courseId, onUpdate }) => {
  const [sections, setSections] = useState<SectionWithLessons[]>([]);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [lessonFormData, setLessonFormData] = useState<LessonFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);

  // Load sections when courseId is available
  useEffect(() => {
    if (courseId) {
      loadSections();
    }
  }, [courseId]);

  // Load available assessments
  useEffect(() => {
    const loadAssessments = async () => {
      try {
        const result = await getAssessments();
        if (result.success && result.data) {
          setAssessments(result.data.filter(a => a.status === 'active'));
        }
      } catch (err) {
        console.error('Failed to load assessments:', err);
      }
    };
    loadAssessments();
  }, []);

  const loadSections = async () => {
    if (!courseId) return;

    console.log('loadSections called for courseId:', courseId);
    setLoading(true);
    setError(null);

    try {
      const result = await getSections(courseId);
      console.log('getSections API result:', result);

      if (result.success && result.data) {
        const sectionsWithLessons = result.data.map(section => ({
          ...section,
          lessons: section.lessons || [],
          isExpanded: false,
        }));
        console.log('Sections loaded successfully:', sectionsWithLessons);
        setSections(sectionsWithLessons);
        onUpdate?.(sectionsWithLessons);
      } else {
        console.error('Failed to load sections:', result.error);
        setError(result.error?.message || 'Failed to load sections');
      }
    } catch (err: any) {
      console.error('Error loading sections:', err);
      setError(err.message || 'Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = async () => {
    if (!courseId) {
      setError('Course ID is required to add sections');
      return;
    }

    if (!newSectionTitle.trim()) {
      setError('Section title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await addSection(courseId, newSectionTitle.trim());

      if (result.success && result.data) {
        console.log('Add section result:', result.data);

        // Reload all sections instead of manually adding to ensure proper data
        await loadSections();

        setNewSectionTitle('');
        setIsAddingSection(false);
        setSuccess('Section added successfully');

        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error?.message || 'Failed to add section');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add section');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId: number) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, isExpanded: !section.isExpanded }
        : section
    ));
  };

  const handleAddLesson = (sectionId?: number) => {
    // Debug logging
    console.log('handleAddLesson called with sectionId:', sectionId);
    console.log('Available sections:', sections);
    console.log('Sections length:', sections.length);

    // If sectionId is provided, use it; otherwise use first section
    const targetSectionId = sectionId ?? (sections.length > 0 && sections[0].id ? sections[0].id : undefined);
    setSelectedSection(targetSectionId ?? null);

    const section = targetSectionId ? sections.find(s => s.id === targetSectionId) : undefined;

    console.log('Target section ID:', targetSectionId);
    console.log('Found section:', section);

    setLessonFormData({
      title: '',
      section: section?.title || '',
      sectionId: targetSectionId, // This will be a number or undefined
      lessonType: 'Content-Based',
      contentType: 'document',
      lessonDuration: '15 mins',
      description: '',
      skills: [],
      isEditing: false,
    });
  };

  const handleEditLesson = (lesson: Lesson) => {
    setLessonFormData({ ...lesson, isEditing: true });
  };

  const handleSaveLesson = async () => {
    if (!courseId || !lessonFormData) return;

    console.log('handleSaveLesson - lessonFormData:', lessonFormData);
    console.log('handleSaveLesson - sectionId:', lessonFormData.sectionId);
    console.log('handleSaveLesson - sectionId type:', typeof lessonFormData.sectionId);

    if (!lessonFormData.title.trim()) {
      setError('Lesson title is required');
      return;
    }

    if (!lessonFormData.sectionId) {
      console.error('Validation failed: sectionId is missing!');
      console.log('Current lessonFormData:', JSON.stringify(lessonFormData, null, 2));
      setError('Please select a section for this lesson');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (lessonFormData.isEditing && lessonFormData.id) {
        // Update existing lesson
        const result = await updateLesson(courseId, Number(lessonFormData.id), lessonFormData);

        if (result.success) {
          setSections(sections.map(section => ({
            ...section,
            lessons: section.lessons.map(lesson =>
              lesson.id === lessonFormData.id ? lessonFormData : lesson
            ),
          })));
          setSuccess('Lesson updated successfully');
        } else {
          setError(result.error?.message || 'Failed to update lesson');
        }
      } else {
        // Add new lesson
        const result = await addLesson(courseId, lessonFormData);

        if (result.success && result.data) {
          const newLesson: Lesson = {
            ...lessonFormData,
            id: result.data.id,
          };

          setSections(sections.map(section =>
            section.id === lessonFormData.sectionId
              ? { ...section, lessons: [...section.lessons, newLesson] }
              : section
          ));
          setSuccess('Lesson added successfully');
        } else {
          setError(result.error?.message || 'Failed to add lesson');
        }
      }

      setLessonFormData(null);
      setSelectedSection(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (lessonId: number | string) => {
    if (!courseId) return;

    if (!window.confirm('Are you sure you want to delete this lesson?')) return;

    setLoading(true);
    setError(null);

    try {
      const result = await deleteLesson(courseId, Number(lessonId));

      if (result.success) {
        setSections(sections.map(section => ({
          ...section,
          lessons: section.lessons.filter(lesson => lesson.id !== lessonId),
        })));
        setSuccess('Lesson deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error?.message || 'Failed to delete lesson');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete lesson');
    } finally {
      setLoading(false);
    }
  };

  const updateLessonFormField = (field: string, value: any) => {
    if (!lessonFormData) return;
    setLessonFormData({ ...lessonFormData, [field]: value });
  };

  if (!courseId) {
    return (
      <div className="tab-content-wrapper">
        <div className="info-message">
          <p>Please save the basic course information first before adding sections and lessons.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content-wrapper">
      <div className="tab-header">
        <h2>Course Sections & Lessons</h2>
        <p className="tab-subtitle">
          Organize your course content into sections and add lessons to each section.
        </p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="close-btn">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <p>{success}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {!isAddingSection && (
          <button
            onClick={() => setIsAddingSection(true)}
            className="btn-primary"
          >
            <Plus size={18} />
            Add Section
          </button>
        )}

        {sections.length > 0 && !isAddingSection && (
          <button
            onClick={() => handleAddLesson()}
            className="btn-secondary"
            style={{
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none'
            }}
          >
            <Plus size={18} />
            Add Lesson
          </button>
        )}
      </div>

      {/* Add Section Form */}
      {isAddingSection && (
        <div className="form-card" style={{ marginBottom: '20px' }}>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Section Title *</label>
              <input
                type="text"
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                placeholder="e.g., Introduction to Programming"
                className="form-input"
              />
            </div>
          </div>
          <div className="form-actions">
            <button
              onClick={handleAddSection}
              disabled={loading}
              className="btn-primary"
            >
              <Save size={18} />
              {loading ? 'Saving...' : 'Save Section'}
            </button>
            <button
              onClick={() => {
                setIsAddingSection(false);
                setNewSectionTitle('');
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Lessons Summary Box */}
      {sections.length > 0 && (
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '15px 20px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e0e0e0'
        }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px', color: '#333' }}>
            Course Content Overview
          </h3>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            Total Sections: <strong>{sections.length}</strong> |
            Total Lessons: <strong>{sections.reduce((sum, s) => sum + s.lessons.length, 0)}</strong>
          </p>
        </div>
      )}

      {/* Sections List */}
      <div className="sections-list">
        {sections.length === 0 && !loading && (
          <div className="empty-state">
            <p>No sections added yet. Click "Add Section" to get started.</p>
          </div>
        )}

        {sections.map((section, index) => (
          <div key={section.id} className="section-card" style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            marginBottom: '15px',
            overflow: 'hidden'
          }}>
            <div
              className="section-header"
              onClick={() => section.id && toggleSection(section.id)}
              style={{
                cursor: 'pointer',
                padding: '15px 20px',
                backgroundColor: '#fff',
                borderBottom: section.isExpanded ? '1px solid #ddd' : 'none'
              }}
            >
              <div className="section-title-wrapper" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {section.isExpanded ? (
                  <ChevronDown size={20} style={{ color: '#FF8C00' }} />
                ) : (
                  <ChevronRight size={20} style={{ color: '#666' }} />
                )}
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                  flex: 1
                }}>
                  Section {index + 1}: {section.title}
                </h3>
                <span className="lesson-count" style={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  {section.lessons.length} lesson{section.lessons.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  section.id && handleAddLesson(section.id);
                }}
                className="btn-icon"
                title="Add Lesson to this Section"
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Plus size={18} />
              </button>
            </div>

            {section.isExpanded && (
              <div className="section-content" style={{
                backgroundColor: '#fafafa',
                padding: '15px 20px'
              }}>
                {section.lessons.length === 0 ? (
                  <div className="empty-lessons" style={{
                    textAlign: 'center',
                    padding: '30px',
                    color: '#999'
                  }}>
                    <p>No lessons in this section yet. Click the + button above to add lessons.</p>
                  </div>
                ) : (
                  <div className="lessons-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {section.lessons.map((lesson, lessonIndex) => (
                      <div key={lesson.id} className="lesson-item" style={{
                        backgroundColor: 'white',
                        padding: '15px',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                      }}>
                        <div className="lesson-info" style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px'
                        }}>
                          <span className="lesson-number" style={{
                            backgroundColor: '#FF8C00',
                            color: 'white',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}>
                            {lessonIndex + 1}
                          </span>
                          <div className="lesson-details" style={{ flex: 1 }}>
                            <h4 style={{
                              margin: '0 0 6px 0',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#333'
                            }}>
                              {lesson.title}
                            </h4>
                            <div className="lesson-meta" style={{
                              display: 'flex',
                              gap: '10px',
                              alignItems: 'center'
                            }}>
                              <span className="badge" style={{
                                backgroundColor: lesson.contentType === 'quiz' ? '#ff6f00' : lesson.lessonType === 'ILTS' ? '#9c27b0' : '#2196f3',
                                color: 'white',
                                padding: '3px 10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {lesson.contentType === 'quiz' ? 'Quiz/Assessment' : lesson.lessonType}
                              </span>
                              {lesson.lessonType === 'Content-Based' && lesson.contentType && (
                                <span className="badge badge-secondary" style={{
                                  backgroundColor: '#f5f5f5',
                                  color: '#666',
                                  padding: '3px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  textTransform: 'uppercase'
                                }}>
                                  {lesson.contentType}
                                </span>
                              )}
                              <span className="duration" style={{
                                color: '#666',
                                fontSize: '13px'
                              }}>
                                {lesson.lessonDuration}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="lesson-actions" style={{
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <button
                            onClick={() => handleEditLesson(lesson)}
                            className="btn-icon"
                            title="Edit Lesson"
                            style={{
                              padding: '8px',
                              backgroundColor: '#f5f5f5',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#666'
                            }}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => lesson.id && handleDeleteLesson(lesson.id)}
                            className="btn-icon btn-danger"
                            title="Delete Lesson"
                            style={{
                              padding: '8px',
                              backgroundColor: '#ffebee',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#d32f2f'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lesson Form Modal */}
      {lessonFormData && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h3>{lessonFormData.isEditing ? 'Edit Lesson' : 'Add Lesson'}</h3>
              <button onClick={() => setLessonFormData(null)} className="btn-icon">
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Section Selector - Show only when adding new lesson */}
              {!lessonFormData.isEditing && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Select Section *</label>
                    <select
                      value={lessonFormData.sectionId || ''}
                      onChange={(e) => {
                        const sectionId = parseInt(e.target.value);
                        const section = sections.find(s => s.id === sectionId);
                        updateLessonFormField('sectionId', sectionId);
                        updateLessonFormField('section', section?.title || '');
                        setSelectedSection(sectionId);
                      }}
                      className="form-select"
                      style={{
                        padding: '10px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {sections.map((section, index) => (
                        <option key={section.id} value={section.id}>
                          Section {index + 1}: {section.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Lesson Title *</label>
                  <input
                    type="text"
                    value={lessonFormData.title}
                    onChange={(e) => updateLessonFormField('title', e.target.value)}
                    placeholder="Enter lesson title"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Lesson Type *</label>
                  <select
                    value={lessonFormData.lessonType || 'Content-Based'}
                    onChange={(e) => {
                      const newLessonType = e.target.value as 'ILTS' | 'Content-Based';
                      console.log('Lesson type changed to:', newLessonType);
                      updateLessonFormField('lessonType', newLessonType);
                      if (newLessonType === 'ILTS') {
                        updateLessonFormField('iltsType', 'Online');
                      } else {
                        updateLessonFormField('contentType', 'document');
                      }
                    }}
                    className="form-select"
                    style={{
                      padding: '10px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  >
                    <option value="Content-Based">Content-Based</option>
                    <option value="ILTS">Instructor-Led Training Session (ILTS)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Duration</label>
                  <input
                    type="text"
                    value={lessonFormData.lessonDuration || ''}
                    onChange={(e) => updateLessonFormField('lessonDuration', e.target.value)}
                    placeholder="e.g., 30 mins"
                    className="form-input"
                  />
                </div>
              </div>

              {lessonFormData.lessonType === 'Content-Based' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Content Type *</label>
                      <select
                        value={lessonFormData.contentType || 'document'}
                        onChange={(e) => {
                          const newContentType = e.target.value;
                          console.log('Content type changed to:', newContentType);
                          updateLessonFormField('contentType', newContentType);
                          // Reset file/URL fields when changing content type
                          updateLessonFormField('contentUrl', '');
                          updateLessonFormField('lessonContentDocument', '');
                          updateLessonFormField('videoUpload', '');
                          updateLessonFormField('scormPackage', '');
                        }}
                        className="form-select"
                        style={{
                          padding: '10px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          width: '100%'
                        }}
                      >
                        <option value="document">Document (PDF/DOC)</option>
                        <option value="mp4">Video (MP4)</option>
                        <option value="scorm">SCORM Package</option>
                        <option value="url">External URL</option>
                        <option value="quiz">Quiz / Assessment</option>
                      </select>
                    </div>
                  </div>

                  {lessonFormData.contentType === 'quiz' ? (
                    <>
                      {/* Assessment/Quiz Selector */}
                      <div className="form-row">
                        <div className="form-group">
                          <label>Select Assessment / Quiz *</label>
                          <select
                            value={lessonFormData.assessmentId || ''}
                            onChange={(e) => {
                              const assessmentId = e.target.value ? parseInt(e.target.value) : undefined;
                              updateLessonFormField('assessmentId', assessmentId);
                            }}
                            className="form-select"
                            style={{
                              padding: '10px',
                              fontSize: '14px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              backgroundColor: '#fff',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              width: '100%'
                            }}
                          >
                            <option value="">-- Select an Assessment --</option>
                            {assessments.map((assessment) => (
                              <option key={assessment.id} value={assessment.id}>
                                {assessment.name} ({assessment.type} - {assessment.questions} questions, {assessment.time})
                              </option>
                            ))}
                          </select>
                          {assessments.length === 0 && (
                            <p style={{ fontSize: '12px', color: '#999', marginTop: '5px', marginBottom: 0 }}>
                              No active assessments available. Please create assessments in the Assessment Management section first.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Assessment Access Control Options */}
                      <div style={{
                        backgroundColor: '#f0f7ff',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #bbdefb',
                        marginTop: '10px'
                      }}>
                        <h4 style={{ margin: '0 0 15px 0', fontSize: '14px', fontWeight: '600', color: '#1565c0' }}>
                          Access Control Settings
                        </h4>

                        {/* Option 1: Require Section Completion */}
                        <div style={{ marginBottom: '15px' }}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333'
                          }}>
                            <input
                              type="checkbox"
                              checked={lessonFormData.requireSectionCompletion || false}
                              onChange={(e) => updateLessonFormField('requireSectionCompletion', e.target.checked)}
                              style={{ marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <div>
                              <strong>Require all lessons in section to be completed</strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                                Users must complete all other lessons in this section before they can access this quiz/assessment.
                              </p>
                            </div>
                          </label>
                        </div>

                        {/* Option 2: Date Range */}
                        <div>
                          <label style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            marginBottom: '10px'
                          }}>
                            <input
                              type="checkbox"
                              checked={!!(lessonFormData.assessmentStartDate || lessonFormData.assessmentEndDate)}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  updateLessonFormField('assessmentStartDate', '');
                                  updateLessonFormField('assessmentEndDate', '');
                                }
                              }}
                              style={{ marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <div>
                              <strong>Restrict to specific date range</strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                                Users can only take this quiz/assessment within the specified start and end dates.
                              </p>
                            </div>
                          </label>

                          <div className="form-row" style={{ marginLeft: '28px', marginTop: '10px' }}>
                            <div className="form-group">
                              <label style={{ fontSize: '13px' }}>Start Date</label>
                              <input
                                type="date"
                                value={lessonFormData.assessmentStartDate || ''}
                                onChange={(e) => updateLessonFormField('assessmentStartDate', e.target.value)}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '13px' }}>End Date</label>
                              <input
                                type="date"
                                value={lessonFormData.assessmentEndDate || ''}
                                onChange={(e) => updateLessonFormField('assessmentEndDate', e.target.value)}
                                className="form-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : lessonFormData.contentType === 'url' ? (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Content URL</label>
                        <input
                          type="url"
                          value={lessonFormData.contentUrl || ''}
                          onChange={(e) => updateLessonFormField('contentUrl', e.target.value)}
                          placeholder="https://example.com/content"
                          className="form-input"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Upload {lessonFormData.contentType === 'mp4' ? 'Video' : lessonFormData.contentType === 'document' ? 'Document' : 'SCORM Package'}</label>
                        <div style={{
                          border: '2px dashed #ccc',
                          borderRadius: '8px',
                          padding: '30px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: '#fafafa',
                          transition: 'all 0.2s'
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const files = e.dataTransfer.files;
                          if (files && files[0]) {
                            const fileField = lessonFormData.contentType === 'mp4' ? 'videoUpload' :
                                            lessonFormData.contentType === 'document' ? 'lessonContentDocument' :
                                            'scormPackage';
                            updateLessonFormField(fileField, files[0].name);
                          }
                        }}
                        >
                          <input
                            type="file"
                            id="content-upload"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const fileField = lessonFormData.contentType === 'mp4' ? 'videoUpload' :
                                                lessonFormData.contentType === 'document' ? 'lessonContentDocument' :
                                                'scormPackage';
                                updateLessonFormField(fileField, file.name);
                              }
                            }}
                            accept={
                              lessonFormData.contentType === 'mp4' ? 'video/*' :
                              lessonFormData.contentType === 'document' ? '.pdf,.doc,.docx' :
                              '.zip'
                            }
                            style={{ display: 'none' }}
                          />
                          <label htmlFor="content-upload" style={{ cursor: 'pointer', width: '100%' }}>
                            <div style={{ color: '#666', marginBottom: '10px', fontSize: '14px' }}>
                              {(() => {
                                const fileField = lessonFormData.contentType === 'mp4' ? lessonFormData.videoUpload :
                                               lessonFormData.contentType === 'document' ? lessonFormData.lessonContentDocument :
                                               lessonFormData.scormPackage;

                                if (fileField) {
                                  // Handle both File object and string
                                  const displayName = typeof fileField === 'string' ? fileField : (fileField as File).name;
                                  return (
                                    <div style={{ color: '#4CAF50', fontWeight: '500' }}>
                                      ✓ {displayName}
                                    </div>
                                  );
                                }
                                return (
                                  <>
                                    <div style={{ fontSize: '16px', marginBottom: '8px' }}>📁 Click to upload or drag and drop</div>
                                    <div style={{ fontSize: '12px', color: '#999' }}>
                                      {lessonFormData.contentType === 'mp4' && 'MP4, MOV, AVI up to 500MB'}
                                      {lessonFormData.contentType === 'document' && 'PDF, DOC, DOCX up to 50MB'}
                                      {lessonFormData.contentType === 'scorm' && 'ZIP file containing SCORM package'}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </label>
                        </div>
                        <p style={{ fontSize: '12px', color: '#999', marginTop: '10px', marginBottom: 0 }}>
                          Note: File will be uploaded after the lesson is saved
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {lessonFormData.lessonType === 'ILTS' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Session Type *</label>
                      <select
                        value={lessonFormData.iltsType || 'Online'}
                        onChange={(e) => {
                          console.log('ILTS type changed to:', e.target.value);
                          updateLessonFormField('iltsType', e.target.value);
                          // Clear venue/meeting URL when switching types
                          if (e.target.value === 'Online') {
                            updateLessonFormField('eventVenue', '');
                          } else {
                            updateLessonFormField('meetUrl', '');
                          }
                        }}
                        className="form-select"
                        style={{
                          padding: '10px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '6px'
                        }}
                      >
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date & Time</label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type="date"
                          value={lessonFormData.startDate || ''}
                          onChange={(e) => updateLessonFormField('startDate', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <input
                          type="time"
                          value={lessonFormData.startTime || ''}
                          onChange={(e) => updateLessonFormField('startTime', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>End Date & Time</label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type="date"
                          value={lessonFormData.endDate || ''}
                          onChange={(e) => updateLessonFormField('endDate', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <input
                          type="time"
                          value={lessonFormData.endTime || ''}
                          onChange={(e) => updateLessonFormField('endTime', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>
                  </div>

                  {lessonFormData.iltsType === 'Online' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Meeting URL</label>
                        <input
                          type="url"
                          value={lessonFormData.meetUrl || ''}
                          onChange={(e) => updateLessonFormField('meetUrl', e.target.value)}
                          placeholder="https://zoom.us/j/123456789"
                          className="form-input"
                        />
                      </div>
                    </div>
                  )}

                  {lessonFormData.iltsType === 'Offline' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Venue</label>
                        <input
                          type="text"
                          value={lessonFormData.eventVenue || ''}
                          onChange={(e) => updateLessonFormField('eventVenue', e.target.value)}
                          placeholder="Enter venue address"
                          className="form-input"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={lessonFormData.description || ''}
                    onChange={(e) => updateLessonFormField('description', e.target.value)}
                    placeholder="Enter lesson description"
                    className="form-textarea"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleSaveLesson}
                disabled={loading}
                className="btn-primary"
              >
                <Save size={18} />
                {loading ? 'Saving...' : 'Save Lesson'}
              </button>
              <button onClick={() => setLessonFormData(null)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && !lessonFormData && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default SectionsLessonsTab;
